import { Time } from '@internationalized/date';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'react-i18next';

/**
 * useNativeHourInput — adapta el `Time` del picker al `<input type="time">` nativo y decide cuándo confirmar.
 *
 * La rueda de iOS dispara un `change` por cada giro; si eso se propagara al picker (live-commit), el re-render
 * del árbol cerraría la rueda a la primera. El valor se guarda como borrador local y se confirma hacia fuera
 * cuando el usuario termina: al cerrar la rueda (blur) o, antes, al tocar cualquier otra cosa (`pointerdown`
 * fuera del input) o, en último término, al desmontarse. Lo segundo importa porque `pointerdown` siempre precede
 * al `click`: si el usuario pulsa «Guardar» con la rueda aún abierta, el picker ya tiene la hora nueva cuando se
 * ejecuta el guardado. Lo tercero cubre el cierre del popover: `DatePickerShell` (no modal) cierra en ese mismo
 * `pointerdown` y su listener corre antes, así que el input se desmonta en mitad del dispatch y ni el nuestro ni
 * el blur llegan a ejecutarse; el cleanup del efecto confirma entonces lo pendiente con el padre aún montado.
 *
 * Dependencia implícita del commit en desmontaje: el cleanup es pasivo, corre DESPUÉS de los `setState` del
 * handler que cerró el diálogo. Los consumidores que revierten el borrador al cancelar (p. ej. `useLessonItemCard`
 * o `useCardResourceTest` con `DatePickerInline`) no se ven pisados porque el picker no se desmonta de forma
 * síncrona en ese mismo commit: la transición de salida del `Dialog` deja renders intermedios en los que el
 * borrador ya se ha re-sincronizado con el valor revertido. Montar el picker con `{isOpen && <DatePickerInline/>}`
 * rompería esa garantía y el cleanup emitiría la hora abandonada sobre el revert.
 */

type UseNativeHourInputParams = {
  onChange: (time: Time | null) => void;
  value: Time | null;
};

type UseNativeHourInputReturn = {
  inputRef: React.RefObject<HTMLInputElement>;
  nativeValue: string;
  onNativeBlur: () => void;
  onNativeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  shouldShowPlaceholder: boolean;
  t: TFunction;
};

function toNativeValue(value: Time | null): string {
  if (!value) return '';
  return `${String(value.hour).padStart(2, '0')}:${String(value.minute).padStart(2, '0')}`;
}

export default function useNativeHourInput({ onChange, value }: UseNativeHourInputParams): UseNativeHourInputReturn {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const committedValue = toNativeValue(value);
  const [draftValue, setDraftValue] = React.useState<string>(committedValue);
  // Último borrador emitido: hace idempotente commitDraft cuando dos vías (pointerdown + desmontaje) caen en el
  // mismo lote y el cleanup reejecuta una closure anterior al re-render (hoy no ocurre en React 17 con listeners
  // nativos; con automatic batching de React 18 sería el camino normal).
  const lastEmittedRef = React.useRef<string | null>(null);

  // Re-sync del borrador cuando el valor cambia desde fuera (mismo patrón render-time que DatePicker).
  const [prevCommittedValue, setPrevCommittedValue] = React.useState<string>(committedValue);
  if (committedValue !== prevCommittedValue) {
    setPrevCommittedValue(committedValue);
    setDraftValue(committedValue);
    lastEmittedRef.current = null;
  }

  function onNativeChange(event: React.ChangeEvent<HTMLInputElement>): void {
    setDraftValue(event.target.value);
  }

  function commitDraft(): void {
    if (draftValue === committedValue || draftValue === lastEmittedRef.current) return;
    lastEmittedRef.current = draftValue;
    if (!draftValue) {
      onChange(null);
      return;
    }
    const [hour, minute] = draftValue.split(':').map(Number);
    onChange(new Time(hour, minute));
  }

  function onNativeBlur(): void {
    commitDraft();
  }

  // Confirma el borrador pendiente en cuanto el usuario toca fuera del input (capture: antes de que el
  // destino procese su propio evento). Solo se escucha mientras hay algo que confirmar.
  React.useEffect(() => {
    if (draftValue === committedValue) return undefined;
    function handlePointerDown(event: PointerEvent): void {
      if (inputRef.current?.contains(event.target as Node)) return;
      commitDraft();
    }
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  });

  // Último recurso: si el input desaparece (el popover se cierra) con un borrador sin confirmar, se confirma
  // en el cleanup. La ref apunta siempre al commitDraft del último render (valores frescos sin deps).
  const commitDraftRef = React.useRef(commitDraft);
  commitDraftRef.current = commitDraft;
  React.useEffect(() => () => commitDraftRef.current(), []);

  // iOS deja el <input type="time"> vacío totalmente en blanco (sin el "--:--" que sí pinta Chrome);
  // el placeholder propio unifica el aspecto con los segmentos de escritorio.
  const shouldShowPlaceholder = draftValue === '';

  return { inputRef, nativeValue: draftValue, onNativeBlur, onNativeChange, shouldShowPlaceholder, t };
}
